import { useNavigate } from 'react-router-dom';
import { Button, Card, Lock, Skeleton, StatusPill } from '@/components/ui';
import { useT } from '@/i18n';
import { formatDate, formatMoney } from '@/lib/format';
import type { GuidanceNoteView, ReportDetail } from '@/api/types';
import { ReportActions } from './ReportActions';

export interface GuidanceViewProps {
  data: ReportDetail;
  unlockPrice?: { amount: number; currency: string };
  retrying: boolean;
  onRetry: () => void;
}

/** Route 3's guidance note: summary and concern titles open, the rest after unlock. Never shows
 * bands or area labels — the banner says on every view that this is not an assessment. */
export const GuidanceView = ({ data, unlockPrice, retrying, onRetry }: GuidanceViewProps) => {
  const t = useT();
  const navigate = useNavigate();
  const { report, content } = data;
  const note: GuidanceNoteView | undefined = content.guidance;
  const unlocked = content.tier === 'full';

  return (
    <div className="mx-auto w-full max-w-narrow">
      <div role="note" className="rounded-md bg-attention-soft px-4 py-3 text-label font-bold text-attention-ink">
        {t('guidance.banner')}
      </div>

      <header className="mt-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-h1 text-ink">{t('guidance.title', { name: report.childName })}</h1>
          <p className="mt-1 text-caption text-ink-muted">
            {t('guidance.prepared', { date: formatDate(report.createdAt) })} · {report.ref}
          </p>
        </div>
        <StatusPill status={report.status} />
      </header>

      {report.status === 'failed' ? (
        <Card tone="sage" className="mt-5">
          <p className="text-label text-ink">{t('gen.failed')}</p>
          <p className="mt-1 text-caption text-ink-muted">{t('gen.failedBody')}</p>
          <Button variant="secondary" className="mt-3" disabled={retrying} onClick={onRetry}>
            {t('gen.retry')}
          </Button>
        </Card>
      ) : !note ? (
        <div className="mt-5">
          <Skeleton lines={5} />
        </div>
      ) : note.status === 'safety' ? (
        <Card className="mt-5 border-2 border-alert bg-alert-soft">
          <p role="alert" className="text-body font-bold text-alert-ink">
            {t('guidance.safety', { name: note.helpline.name, number: note.helpline.number })}
          </p>
        </Card>
      ) : note.status === 'out_of_scope' ? (
        <Card tone="sage" className="mt-5">
          <p className="text-body text-ink">{t('guidance.outOfScope')}</p>
        </Card>
      ) : (
        <>
          <Card className="mt-5 bg-sage-100">
            <h2 className="text-h3 text-ink">{t('guidance.inShort')}</h2>
            <p className="mt-2 text-body text-ink">{note.summary}</p>
          </Card>

          <ol className="mt-4 space-y-3">
            {note.sections.map((section, index) => (
              <li key={index}>
                <Card>
                  <h3 className="text-h3 text-ink">
                    {index + 1}. {section.concern}
                  </h3>
                  {note.locked ? (
                    <p className="mt-2 flex items-center gap-2 text-caption text-ink-muted">
                      <Lock aria-hidden="true" className="h-4 w-4" />
                      {t('guidance.lockedSection')}
                    </p>
                  ) : (
                    <>
                      <p className="mt-3 text-label uppercase tracking-wide text-ink-muted">{t('guidance.canMean')}</p>
                      <p className="mt-1 text-body text-ink">{section.what_it_can_mean}</p>
                      <p className="mt-3 text-label uppercase tracking-wide text-ink-muted">{t('guidance.tryAtHome')}</p>
                      <ul className="mt-1 list-disc space-y-1 pl-5 text-body text-ink">
                        {(section.try_at_home ?? []).map((idea, i) => (
                          <li key={i}>{idea}</li>
                        ))}
                      </ul>
                      <p className="mt-3 rounded-md bg-provider-soft px-3 py-2 text-caption text-ink">
                        <strong>{t('guidance.seekHelp')}</strong> {section.when_to_seek_help}
                      </p>
                    </>
                  )}
                </Card>
              </li>
            ))}
          </ol>

          {!unlocked && (
            <Card className="mt-5">
              <h2 className="text-h3 text-ink">{t('guidance.unlockTitle')}</h2>
              <p className="mt-1 text-caption text-ink-muted">{t('guidance.unlockBody')}</p>
              {unlockPrice && (
                <p className="mt-3 font-display text-[32px] font-extrabold leading-none text-ink">
                  {formatMoney(unlockPrice.amount, unlockPrice.currency)}
                </p>
              )}
              <Button variant="celebrate" full className="mt-4" onClick={() => navigate(`/sessions/${report.assessmentId}/checkout`)}>
                {t('checkout.title')}
              </Button>
            </Card>
          )}

          {unlocked && note.next_steps.length > 0 && (
            <Card className="mt-4">
              <h2 className="text-h3 text-ink">{t('guidance.nextSteps')}</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-body text-ink">
                {note.next_steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </Card>
          )}

          <p className="mt-5 text-caption text-ink-muted">{note.handoff || content.disclaimer}</p>

          <ReportActions report={report} tier={content.tier} className="mt-5 rounded-xl bg-white p-5 shadow-1" />
        </>
      )}

      <Card tone="sage" className="mt-6">
        <p className="text-label text-ink">{t('guidance.checkinTitle')}</p>
        <p className="mt-1 text-caption text-ink-muted">{t('guidance.checkinBody', { name: report.childName })}</p>
        <Button variant="secondary" className="mt-3" onClick={() => navigate('/children')}>
          {t('guidance.checkinCta')}
        </Button>
      </Card>
    </div>
  );
};
